import React, { useState } from "react";
import { useApp } from "context";
import {
  Box,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import { Custom, Item } from "components";
import { Constants, Enums, Helpers } from "utils";
import { IAppointment, IGoal, IQarePlan } from "interfaces";
import RoomIcon from "@mui/icons-material/Room";
import SettingsIcon from "@mui/icons-material/Settings";

type Props = {
  sx?: any;
  appointment: IAppointment;
  handleQarePlanOpen: (qarePlan: IQarePlan) => void;
  getDateTime: (scheduledTime: Date) => React.ReactNode;
};

export const Appointment = (props: Props) => {
  const { t, theme, locale, service } = useApp();

  const [healthGoals, setHealthGoals] = useState<IGoal[]>([]);
  const [anchorElement, setAnchorElement] = useState(null);
  // TODO: it may be improved (currently being used to force the updated of the appointments)
  const [incrementComponent, setIncrementComponent] = useState(0);

  const toDate = props.appointment.scheduledTime
    ? new Date(props.appointment.scheduledTime)
    : null;

  const date = toDate
    ? toDate.toLocaleDateString(locale, Constants.DATE_FORMAT)
    : "";

  const time = toDate
    ? toDate.toLocaleTimeString(locale, Constants.TIME_FORMAT)
    : "";

  const isDone = toDate ? toDate.getTime() < Date.now() : false;

  const title =
    props.appointment.provider?.username ?? props.appointment.clinic.username;

  const handleOpen = async () => {
    const [status, response] = await service.goal.all();
    setHealthGoals(response);
  };

  const handleAnchorElement = async (event: any) => {
    await handleOpen();
    setAnchorElement(anchorElement ? null : event.target);
  };

  const handleHealthGoalAdd = async (healthGoalId: number | string) => {
    const [status, response] = await service.goal.add(
      Number(healthGoalId),
      props.appointment.appointmentId
    );
    if (status === Enums.EnumResponse.Success) {
      const goal = healthGoals.find(
        (item: IGoal) => item.healthGoalId === healthGoalId
      );
      if (goal) {
        props.appointment.healthGoals.push(goal);
        setIncrementComponent(incrementComponent + 1);
      }
    }
  };

  const handleHealthGoalDelete = async (healthGoalId: number | string) => {
    const [status, response] = await service.goal.remove(
      Number(healthGoalId),
      props.appointment.appointmentId
    );
    if (status === Enums.EnumResponse.Success) {
      const index = props.appointment.healthGoals.findIndex(
        (item: IGoal) => item.healthGoalId === healthGoalId
      );
      if (index >= 0) {
        props.appointment.healthGoals.splice(index, 1);
        setIncrementComponent(incrementComponent + 1);
      }
    }
  };

  const handleCheckbox = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
    value: number | undefined
  ) => {
    if (!value) return;
    checked ? handleHealthGoalAdd(value) : handleHealthGoalDelete(value);
  };

  // TODO: needs to be fetched from DB for each clinic
  const googleMapURL = "https://goo.gl/maps/F2anBd384D6EyV7C9";

  return (
    <>
      <Custom.Card sx={{ ...props.sx, marginBottom: 0 }}>
        <Grid container spacing={theme.margin.sm}>
          <Grid item xs={12}>
            <Custom.Box variant="filled">
              <div>
                {props.getDateTime(new Date(props.appointment.scheduledTime))}
              </div>
              <Custom.Button
                variant="outlined"
                disabled={!props.appointment.qarePlan}
                sx={{ textTransform: "none" }}
                onClick={() => {
                  props.handleQarePlanOpen(
                    props.appointment.qarePlan as IQarePlan
                  );
                }}
              >
                {t.title.myqare_plan}
              </Custom.Button>
            </Custom.Box>
          </Grid>
          <Grid item xs={12}>
            <Custom.Box variant="filled">
              <Item.ServiceIcon serviceId={props.appointment.serviceId} />
              <div>{props.appointment.service.serviceName}</div>
            </Custom.Box>
          </Grid>
          <Grid item xs={12}>
            <Custom.Box variant="filled">
              <div>{props.appointment.provider?.specialty}</div>
              <div>
                {Helpers.replaceComma(props.appointment.provider?.username)}
              </div>
            </Custom.Box>
          </Grid>
          <Grid item xs={12}>
            <Custom.Box variant="filled">
              <div>{props.appointment.clinic.username}</div>
              <IconButton onClick={() => window.open(googleMapURL, "_blank")}>
                <RoomIcon />
              </IconButton>
            </Custom.Box>
          </Grid>
        </Grid>
        {/* <CardHeader
          sx={{ p: 0 }}
          avatar={<Custom.Avatar name={title} />}
          // action={
          //   <IconButton>
          //     <CrisisAlertIcon sx={{ color: theme.palette.font.color }} />
          //   </IconButton>
          // }
          title={
            <Typography
              component="div"
              sx={{
                textAlign: "left",
                fontSize: theme.font.lg,
                color: theme.palette.font.color,
              }}
            >
              {title}
            </Typography>
          }
          subheader={
            <Typography
              component="div"
              sx={{
                textAlign: "left",
                fontSize: theme.font.md,
                color: theme.palette.font.accent,
              }}
            >
              {t.service[Helpers.getServiceName(props.appointment.serviceId)]}
            </Typography>
          }
        /> */}
        {/* <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginTop: theme.margin.md,
          }}
        >
          <Typography
            component="div"
            sx={{
              lineHeight: 1,
              fontSize: theme.font.md,
              fontWeight: theme.font.normal,
              color: theme.palette.font.color,
            }}
          >
            {date}
          </Typography>
          &nbsp;
          <Typography
            component="div"
            sx={{
              fontSize: theme.font.md,
              color: theme.palette.font.accent,
            }}
          >
            {`${t.conjunction.at} ${time}`}
          </Typography>
        </Box> */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginTop: theme.margin.md,
          }}
        >
          <Grid container spacing={theme.margin.sm}>
            <Grid item key={-1}>
              <Custom.Button
                size="small"
                onClick={handleAnchorElement}
                sx={{ lineHeight: 1 }}
              >
                {t.label.health_goal}
                <SettingsIcon
                  sx={{
                    marginLeft: theme.margin.xs,
                  }}
                />
              </Custom.Button>
            </Grid>
            {props.appointment.healthGoals &&
              props.appointment.healthGoals.map((healthGoal: IGoal) => {
                return (
                  <Grid item key={healthGoal.healthGoalId}>
                    <Custom.Chip
                      label={healthGoal.healthGoal}
                      id={healthGoal.healthGoalId}
                      onDelete={handleHealthGoalDelete}
                    />
                  </Grid>
                );
              })}
          </Grid>
        </Box>
      </Custom.Card>
      <Popper anchorEl={anchorElement} open={!!anchorElement}>
        <Paper
          sx={{
            maxWidth: 300,
            padding: theme.margin.md,
            color: theme.palette.font.color,
            backgroundColor: theme.palette.background.accent,
          }}
        >
          <Typography
            component="div"
            sx={{ fontSize: theme.font.md, paddingBottom: theme.margin.sm }}
          >
            {t.label.health_goal}
          </Typography>
          <Box
            sx={{
              maxHeight: 150,
              overflowY: "auto",
            }}
            className="hide-scrollbar"
          >
            <Grid container>
              {healthGoals.map((healthGoal: IGoal) => {
                return (
                  <Grid xs={12} item key={healthGoal.healthGoalId}>
                    <Custom.Checkbox
                      id={healthGoal.healthGoalId}
                      label={healthGoal.healthGoal}
                      onChange={handleCheckbox}
                      checked={props.appointment.healthGoals
                        ?.map((item) => item.healthGoalId)
                        .includes(healthGoal.healthGoalId)}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Paper>
      </Popper>
    </>
  );
};
